import clsx from 'clsx'
import React from 'react'
import {
    FieldValues,
    SubmitHandler,
    UseFormHandleSubmit,
} from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import Button from './inputs/Button'
import Note from '../note/Note'
import { NoteType } from '../../../enums/NoteType'
import SupportEmailLink from '../links/SupportEmailLink'

type Props<TFormData extends FieldValues> = {
    children: React.ReactNode | React.ReactNode[]
    className?: string
    apiError?: string
    entityName: string
    cancelTo?: string
    handleSubmit: UseFormHandleSubmit<TFormData>
    onSubmit: SubmitHandler<TFormData>
    isSubmitting: boolean
    submitButtonContent?:
        | React.ReactNode
        | ((isSubmitting: boolean) => React.ReactNode)
}

function Form<TFormData extends FieldValues>({
    children,
    className,
    apiError,
    entityName,
    cancelTo,
    handleSubmit,
    onSubmit,
    isSubmitting,
    submitButtonContent,
}: Props<TFormData>): React.JSX.Element {
    const classes = clsx('', className)
    const renderSubmitButtonContent = (): React.ReactNode => {
        if (typeof submitButtonContent === 'function') {
            return submitButtonContent(isSubmitting)
        }

        if (submitButtonContent) {
            return submitButtonContent
        }

        return (
            <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {isSubmitting ? 'Saving…' : 'Save'}
            </>
        )
    }

    return (
        <form
            className="max-w-[600px] mx-auto"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className={classes}>
                {children}

                <div className="mt-6 flex flex-col gap-2 w-full">
                    <div
                        className="flex flex-col gap-2 
                  md:flex-row-reverse md:justify-start md:gap-4"
                    >
                        <Button
                            type="submit"
                            style="primary"
                            classes="w-full md:w-auto"
                        >
                            {renderSubmitButtonContent()}
                        </Button>

                        {cancelTo && (
                            <Button
                                type="link"
                                to={cancelTo}
                                style="mild-danger"
                                classes="w-full md:w-auto"
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>

                {apiError && (
                    <Note type={NoteType.ERROR} containerClassNames="mb-4">
                        <div>
                            There was a problem saving the {entityName}. Please
                            try again or contact <SupportEmailLink /> if the
                            problem persists.
                        </div>
                        <div className="mt-2">Error details: {apiError}</div>
                    </Note>
                )}
            </div>
        </form>
    )
}

export default Form
